// src/components/admin/NewEmployeeDialog.jsx
import React, { useEffect, useState } from 'react';
import * as usersApi from '../../services/usersApi';
import Constants from '../Constants/Constants';
import { useAuth } from '../../auth/AuthContext';
import './NewEmployeeDialog.css';

const constants = new Constants();
const EXPERIENCE_OPTIONS = constants.getExperienceList();

const toBool = (v) => {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : v;
  return !(s === 0 || s === '0' || s === false || s === 'false' || s == null);
};
const boolTo10 = (b) => (b ? '1' : '0');

const fieldLabel = { fontSize: 13, color: '#444', marginBottom: 4 };
const input = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #ddd',
  background: '#fff',
  fontSize: 14,
};
const btn = {
  padding: '10px 14px',
  borderRadius: 8,
  border: '1px solid #dfe3e6',
  background: '#fff',
  cursor: 'pointer',
};
const btnPrimary = {
  ...btn,
  border: '1px solid #0a7',
  background: '#0c8',
  color: '#fff',
};

export default function NewEmployeeDialog({
  open,
  onClose,
  onCreated,         // (employee) => {}
  restaurantId = '1' // kept for symmetry (roles list)
}) {
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
 const { tenantServerUrl,user,selectedRestaurantId } = useAuth();
  const [form, setForm] = useState({
    username: '',
    display_name: '',
    email: '',
    phone: '',
    role: '',
    experience: '',
    postal_addr: '',
    physical_addr: '',
    nation: '',
    gender: '',
    position: '',
    disabilities: '',
    comments: '',
    enabled: '1',
    password: '',
    confirm: '',
  });

  useEffect(() => {
    if (!open) return;
    let live = true;
    (async () => {
      if (!usersApi.listRoles) return;
      try {
        setRolesLoading(true);
        const raw = await usersApi.listRoles(tenantServerUrl,{ restaurantId });
        const opts = (raw || []).map(r => ({
          value: String(r?.value ?? r?.code ?? r?.id ?? r),
          label: String(r?.label ?? r?.name ?? r),
        })).filter(o => o.value);
        if (live) setRoles(opts);
      } finally { if (live) setRolesLoading(false); }
    })();
    return () => { live = false; };
  }, [open, restaurantId]);

  const setVal = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr('');

    if (!form.username.trim()) { setErr('Username is required.'); return; }
    if (!form.display_name.trim()) { setErr('Full name is required.'); return; }
    if (!form.email.trim()) { setErr('Email is required.'); return; }
    if (form.password || form.confirm) {
      if (form.password !== form.confirm) { setErr('Passwords do not match.'); return; }
      if (form.password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    }

    const bywho =
      localStorage.getItem('loggedUser_Username') ||
      localStorage.getItem('loggedUser_Email') ||
      'admin';

    const payload = {
      username:  form.username.trim(),
      name:      form.display_name.trim(),
      email:     form.email.trim(),
      contact:   form.phone.trim(),
      role_id:   form.role || '',
      experience: form.experience || '',
      post:      form.postal_addr || '',
      physical:  form.physical_addr || '',
      nation:    form.nation || '',
      gender:    form.gender || '',
      position:  form.position || '',
      disabilities: form.disabilities || '',
      comments:  form.comments || '',
      enabled:   form.enabled,                   // '1' | '0'
      password:  form.password || undefined,     // optional
      bywho,
    };

    try {
      setBusy(true);
      const created = await usersApi.addEmployee(tenantServerUrl, payload, { debug: true });
      onCreated?.(created);
      onClose?.();
    } catch (e2) {
      console.error('[NewEmployeeDialog] create failed:', e2);
      setErr(e2?.message || 'Failed to create employee.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="new-employee-dialog"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }}
      onClick={onClose}
    >
      <div
        className="new-employee-dialog-inner"
        style={{
          width: 720, maxWidth: '95vw', background: '#fff', borderRadius: 12,
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)', padding: 16
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>Add New Employee</div>

        <form onSubmit={submit}>
          <div className="new-employee-dialog-form-grid">
            <div>
              <div style={fieldLabel}>Username *</div>
              <input style={input} value={form.username} onChange={e => setVal('username', e.target.value)} />
            </div>
            <div>
              <div style={fieldLabel}>Full Name *</div>
              <input style={input} value={form.display_name} onChange={e => setVal('display_name', e.target.value)} />
            </div>

            <div>
              <div style={fieldLabel}>Email *</div>
              <input type="email" style={input} value={form.email} onChange={e => setVal('email', e.target.value)} />
            </div>
            <div>
              <div style={fieldLabel}>Phone</div>
              <input style={input} value={form.phone} onChange={e => setVal('phone', e.target.value)} />
            </div>

            <div>
              <div style={fieldLabel}>Role *</div>
              <select
                style={input}
                value={form.role}
                onChange={e => setVal('role', e.target.value)}
                disabled={rolesLoading}
                required
              >
                <option value="">{rolesLoading ? 'Loading…' : 'Select role'}</option>
                {roles.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <div style={fieldLabel}>Experience</div>
              <select style={input} value={form.experience} onChange={e => setVal('experience', e.target.value)}>
                <option value="">Select…</option>
                {EXPERIENCE_OPTIONS.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>

            <div>
              <div style={fieldLabel}>Postal Address</div>
              <input style={input} value={form.postal_addr} onChange={e => setVal('postal_addr', e.target.value)} />
            </div>
            <div>
              <div style={fieldLabel}>Physical Address</div>
              <input style={input} value={form.physical_addr} onChange={e => setVal('physical_addr', e.target.value)} />
            </div>

            <div>
              <div style={fieldLabel}>Nationality</div>
              <input style={input} value={form.nation} onChange={e => setVal('nation', e.target.value)} />
            </div>
            <div>
              <div style={fieldLabel}>Gender</div>
              <input style={input} value={form.gender} onChange={e => setVal('gender', e.target.value)} />
            </div>

            <div>
              <div style={fieldLabel}>Position</div>
              <input style={input} value={form.position} onChange={e => setVal('position', e.target.value)} />
            </div>
            <div>
              <div style={fieldLabel}>Enabled</div>
              <select style={input} value={form.enabled} onChange={e => setVal('enabled', e.target.value)}>
                <option value="1">Enabled</option>
                <option value="0">Disabled</option>
              </select>
            </div>

            <div>
              <div style={fieldLabel}>Initial Password (optional)</div>
              <input type="password" style={input} value={form.password} onChange={e => setVal('password', e.target.value)} />
            </div>
            <div>
              <div style={fieldLabel}>Confirm Password</div>
              <input type="password" style={input} value={form.confirm} onChange={e => setVal('confirm', e.target.value)} />
            </div>

            <div className="new-employee-dialog-form-full-width">
              <div style={fieldLabel}>Comments</div>
              <textarea rows={3} style={{ ...input, resize: 'vertical' }} value={form.comments} onChange={e => setVal('comments', e.target.value)} />
            </div>
          </div>

          {!!err && <div style={{ color: '#b3261e', fontSize: 12, marginTop: 8 }}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
            <button type="button" style={btn} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" style={btnPrimary} disabled={busy}>{busy ? 'CREATING…' : 'Create Employee'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
